import React, { useMemo } from "react"
import { Box, Typography, Button } from "@mui/material"
import { Step } from "./types"
import { HelpDrawerProps } from "./types"
import { COPY } from "./constants"
import { useHelpDrawer } from "./useHelpDrawer"
import DescriptionStep from "./DescriptionStep"
import ContactPreferencesStep from "./ContactPreferencesStep"
import ConfirmationStep from "./ConfirmationStep"
import AtticusDrawer from "views/shared/drawer/AtticusDrawer"

const HelpDrawer: React.FC<HelpDrawerProps> = ({ open, onClose }) => {
  const { state, handlers } = useHelpDrawer(onClose)

  const isFinalStep =
    state.currentStep === Step.CONTACT_PREFERENCES &&
    state.showContactInfoForm &&
    state.isContactInfoValid

  const isNextButtonDisabled = useMemo(() => {
    if (state.currentStep === Step.DESCRIPTION) {
      return !state.description.trim()
    }

    if (state.currentStep === Step.CONTACT_PREFERENCES) {
      return !state.showContactInfoForm || !state.isContactInfoValid || state.isSendingFeedback
    }

    return false
  }, [state])

  const isBackButtonDisabled = useMemo(() => {
    return state.isSendingFeedback
  }, [state])

  const drawerContents = useMemo(
    () => (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          pb: 2,
        }}
      >
        {state.currentStep === Step.DESCRIPTION && (
          <DescriptionStep
            description={state.description}
            handleDescriptionChange={handlers.handleDescriptionChange}
            uploadedFiles={state.uploadedFiles}
            setUploadedFiles={handlers.setUploadedFiles}
            isDescriptionValid={state.isDescriptionValid}
            setFileUploadErrors={handlers.setFileUploadErrors}
          />
        )}

        {state.currentStep === Step.CONTACT_PREFERENCES && (
          <ContactPreferencesStep
            existingPhoneNumber={state.existingPhoneNumber}
            existingEmail={state.existingEmail}
            contactPreferences={state.contactPreferences}
            setContactPreferences={handlers.setContactPreferences}
            contactInfo={state.contactInfo}
            setContactInfo={handlers.setContactInfo}
            setIsContactInfoValid={handlers.setIsContactInfoValid}
            isContactInfoValid={state.isContactInfoValid}
            resetValidationError={handlers.resetValidationError}
            showContactInfoForm={state.showContactInfoForm}
            setShowContactInfoForm={handlers.setShowContactInfoForm}
            handleContactInfoChange={handlers.handleContactInfoChange}
          />
        )}

        {state.currentStep === Step.CONFIRMATION && (
          <ConfirmationStep handleFinish={handlers.closeAndReset} />
        )}

        {state.validationError && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {state.validationError}
          </Typography>
        )}

        {state.currentStep !== Step.CONFIRMATION && (
          <Box sx={{ display: "flex", gap: 2, marginLeft: "auto", mt: 2 }}>
            {state.showCloseConfirmation ? (
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body2" color="error" sx={{ alignSelf: "center", mr: 1 }}>
                  {COPY.CLOSE_CONFIRMATION.MESSAGE}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <Button variant="outlined" color="primary" onClick={handlers.handleCancelClose}>
                    {COPY.CLOSE_CONFIRMATION.CANCEL_CLOSE}
                  </Button>
                  <Button variant="contained" color="error" onClick={handlers.handleConfirmClose}>
                    {COPY.CLOSE_CONFIRMATION.CONFIRM_CLOSE}
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                {state.currentStep > Step.DESCRIPTION || state.showContactInfoForm ? (
                  <Button
                    variant="text"
                    color="secondary"
                    onClick={handlers.handleBackStep}
                    disabled={isBackButtonDisabled}
                  >
                    {COPY.BUTTONS.BACK}
                  </Button>
                ) : (
                  <Button variant="text" color="secondary" onClick={handlers.handleCloseAttempt}>
                    {COPY.BUTTONS.CANCEL}
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={isFinalStep ? handlers.handleSubmit : handlers.handleNextStep}
                  disabled={isNextButtonDisabled}
                >
                  {isFinalStep ? COPY.BUTTONS.SUBMIT : COPY.BUTTONS.NEXT}
                </Button>
              </>
            )}
          </Box>
        )}
      </Box>
    ),
    [state, handlers, isFinalStep, isNextButtonDisabled]
  )

  return (
    <AtticusDrawer
      variant="temporary"
      anchor="right"
      open={open}
      onClose={handlers.handleCloseAttempt}
      contents={drawerContents}
    />
  )
}

export default HelpDrawer
